import {CAMPAIGN_KEYS} from "@/api/campaignApi";
import {CAMPAIGN_APPLICATION_KEYS} from "@/api/campaignApplicationApi";
import {client} from "@/httpClient";
import {capturePostHogEvent, PostHogEvents} from "@/posthog";
import {queryClient} from "@/queryClient";
import useApplicationFilterStore from "@/store/applicationFilter";
import {create} from "@/store/createStore";
import {paymentFailedModalDataAtom} from "@/store/jotaiAtoms";
import type {
  TCampaign,
  TCampaignApplicationMinimal,
  TCampaignManagementData,
  TCampaignMetrics,
  TCampaignTab,
  TCampaignType,
  TInvoice,
  TNonFunctionKeys,
  TPackageName,
  TPaginated,
} from "@/store/types";
import useUserStore from "@/store/user";
import type {AxiosResponse} from "axios";
import {getDefaultStore} from "jotai";
import {useMemo} from "react";
import {devtools} from "zustand/middleware";
import {immer} from "zustand/middleware/immer";

type TState = {
  campaign: TCampaign;
  management: TCampaignManagementData;
  reportingDateFilter: string | null;
  tabState: TCampaignTab;
  isBonusPoolPopupOpen: boolean;
  isCampaignStartPopupOpen: boolean;
  isConfirmProCampaignStartPopupOpen: boolean;
  isTrialPopupOpen: boolean;
  applicationToConfirm: TCampaignApplicationMinimal | null;
  applicationToRenegotiate: TCampaignApplicationMinimal | null;
  showRenegotiationDeclineButton: boolean;
  setValue<K extends TNonFunctionKeys<TState>>(key: K, value: TState[K]): void;
  acceptApplicant(applicationId: number): Promise<void>;
  declineApplicant(applicationId: number): Promise<void>;
  undoApplicant(applicationId: number): Promise<void>;
  startCampaign(applicationId?: number): Promise<void>;
  getCampaignType(): TCampaignType;
  getCompanyPlanName(): TPackageName | undefined;
  reset(): void;
};

const initialState = {
  tabState: "overview",
  management: {},
  campaign: {},
  isBonusPoolPopupOpen: false,
  isCampaignStartPopupOpen: false,
  isConfirmProCampaignStartPopupOpen: false,
  isTrialPopupOpen: false,
  reportingDateFilter: null,
  applicationToConfirm: null,
  applicationToRenegotiate: null,
  showRenegotiationDeclineButton: true,
} as Pick<TState, TNonFunctionKeys<TState>>;

const getCampaignApplicationQueryKeys = (campaignId: number) => {
  const searchParams = useApplicationFilterStore.getState().searchParams;

  return [
    CAMPAIGN_KEYS.qCampaignAcceptedApplications(campaignId, `${searchParams}&pro=false`),
    CAMPAIGN_KEYS.qCampaignAcceptedApplications(campaignId, `${searchParams}&pro=true`),
    CAMPAIGN_KEYS.qCampaignDeclinedApplications(campaignId, `${searchParams}&pro=false`),
    CAMPAIGN_KEYS.qCampaignDeclinedApplications(campaignId, `${searchParams}&pro=true`),
    CAMPAIGN_KEYS.qCampaignOpenApplications(campaignId, `${searchParams}&pro=false`),
    CAMPAIGN_KEYS.qCampaignOpenApplications(campaignId, `${searchParams}&pro=true`),
    CAMPAIGN_KEYS.qCampaignRenegotiations(campaignId, searchParams),
  ];
};

const useCampaignManagementStore = create<TState>()(
  immer(
    devtools(
      (set, get) => ({
        ...initialState,
        setValue: (key, value) =>
          set(
            state => {
              state[key] = value;
            },
            false,
            "campaignManagementStore/setValue",
          ),
        acceptApplicant: async applicationId => {
          try {
            await client.post(CAMPAIGN_APPLICATION_KEYS.mApplicationAccept(+applicationId)[0]);
            const campaignId = get().campaign.id!;
            // PostHog event
            const companyId = get().campaign.company_profile?.id;
            const publishedAt = get().campaign.created;
            capturePostHogEvent(PostHogEvents.InfluencerAccepted, {
              companyId,
              campaignId,
              campaignType: get().getCampaignType(),
              plan: get().getCompanyPlanName(),
              publishedAt,
              influencerApplicationId: applicationId,
            });

            // TODO: later can be optimized with optimistic update
            await Promise.all(
              [
                ...getCampaignApplicationQueryKeys(campaignId),
                CAMPAIGN_KEYS.qCampaignManagement(campaignId),
                CAMPAIGN_KEYS.qCampaignManagementStats(campaignId),
              ].map(queryKey => queryClient.refetchQueries({queryKey})),
            );
          } catch (error) {
            console.log(error);
          }
        },
        declineApplicant: async applicationId => {
          try {
            await client.post(CAMPAIGN_APPLICATION_KEYS.mApplicationDecline(+applicationId)[0]);
            const campaignId = get().campaign.id!;
            // PostHog event
            const companyId = get().campaign.company_profile?.id;
            const publishedAt = get().campaign.created;
            capturePostHogEvent(PostHogEvents.InfluencerDeclined, {
              companyId,
              campaignId,
              campaignType: get().getCampaignType(),
              plan: get().getCompanyPlanName(),
              publishedAt,
              influencerApplicationId: applicationId,
            });

            // TODO: later can be optimized with optimistic update
            await Promise.all(
              [...getCampaignApplicationQueryKeys(campaignId), CAMPAIGN_KEYS.qCampaignManagement(campaignId)].map(
                queryKey => queryClient.refetchQueries({queryKey: queryKey}),
              ),
            );
          } catch (error) {
            console.log(error);
          }
        },
        undoApplicant: async applicationId => {
          try {
            await client.post(CAMPAIGN_APPLICATION_KEYS.mApplicationPending(+applicationId)[0]);
            const campaignId = get().campaign.id!; // necessary to undo application
            // PostHog event
            const companyId = get().campaign.company_profile?.id;
            const publishedAt = get().campaign.created;
            capturePostHogEvent(PostHogEvents.RevertInfluencerDecisionBackToPending, {
              companyId,
              campaignId,
              campaignType: get().getCampaignType(),
              plan: get().getCompanyPlanName(),
              publishedAt,
              influencerApplicationId: applicationId,
            });

            // TODO: later can be optimized with optimistic update
            await Promise.all(
              [
                ...getCampaignApplicationQueryKeys(campaignId),
                CAMPAIGN_KEYS.qCampaignManagement(campaignId),
                CAMPAIGN_KEYS.qCampaignManagementStats(campaignId),
              ].map(queryKey => queryClient.refetchQueries({queryKey})),
            );
          } catch (error) {
            console.log(error);
          }
        },
        startCampaign: async applicationId => {
          const campaignId = get().campaign.id!;
          try {
            const res = await client.post(CAMPAIGN_KEYS.mCampaignStart(campaignId)[0], {
              success_url: window.location.href,
              cancel_url: window.location.href,
              campaign_application_id:
                applicationId && get().campaign.event && get().campaign.on_going ? applicationId : undefined,
            });

            if (res.data.stripe_url) {
              window.location.href = res.data.stripe_url;
            }

            setTimeout(async () => {
              await Promise.all(
                [
                  ...getCampaignApplicationQueryKeys(campaignId),
                  CAMPAIGN_KEYS.qCampaign(campaignId),
                  CAMPAIGN_KEYS.qCampaignManagement(campaignId),
                  CAMPAIGN_KEYS.qCampaignPreparationApplications(campaignId),
                ].map(queryKey => queryClient.refetchQueries({queryKey})),
              );

              const management = queryClient.getQueryData<AxiosResponse<TCampaignManagementData>>(
                CAMPAIGN_KEYS.qCampaignManagement(campaignId),
              )?.data;

              // PostHog event
              const companyId = get().campaign.company_profile?.id;
              const publishedAt = get().campaign.created;

              capturePostHogEvent(PostHogEvents.StartCampaign, {
                companyId,
                campaignId,
                campaignType: get().getCampaignType(),
                isOngoingCampaign: management?.on_going,
                isEventCampaign: management?.event,
                influencerApplicationId: applicationId,
                amountOfAcceptedRegularInfluencersIncludingNoLimit: management?.accepted_applications,
                amountOfAcceptedProInfluencers: management?.accepted_applications_pro,
                amountOfAcceptedInfluencersNoLimit: management?.accepted_applications_no_limit,

                amountOfDeclinedRegularInfluencersIncludingNoLimit: management?.declined_applications,
                amountOfDeclinedProInfluencers: management?.declined_applications_pro,
                amountOfDeclinedInfluencersNoLimit: management?.declined_applications_no_limit,

                amountOfOpenApplicationsRegularInfluencers: management?.open_applications,
                amountOfOpenApplicationsProInfluencers: management?.open_applications_pro,
                amountOfOpenApplicationsInfluencersNoLimit: management?.open_applications_no_limit,

                amountOfPreparationApplications: management?.preparation_applications,
                amountOfActiveApplications: management?.active_applications,
                amountOfReportingApplications: management?.reporting_applications,

                plan: get().getCompanyPlanName(),
                publishedAt,
              });

              if (!management?.open_applications && !management?.accepted_applications) {
                const searchParams = new URLSearchParams(window.location.search);
                searchParams.set("tab", get().campaign.preparation_days === 0 ? "active" : "preparation");
                window.history.pushState({}, "", `${window.location.pathname}?${searchParams.toString()}`);
                window.dispatchEvent(new Event("popstate"));
              }
            }, 2000);

            set(
              state => {
                state.isBonusPoolPopupOpen = false;
                state.isCampaignStartPopupOpen = false;
                state.isConfirmProCampaignStartPopupOpen = false;
                state.isTrialPopupOpen = false;
              },
              false,
              "campaignManagementStore/startCampaign",
            );
          } catch (error: any) {
            if (error.response?.data?.detail === "Failed to pay invoice") {
              const stats = queryClient.getQueryData<AxiosResponse<TCampaignMetrics>>(
                CAMPAIGN_KEYS.qCampaignManagementStats(campaignId),
              )?.data;
              const totalCost = Math.max(0, stats?.total_cost!) + stats?.tax!;
              const failedPayments = (
                await client.get<TPaginated<TInvoice>>(`/invoices/failed_payments/?campaign_id=${campaignId}`)
              ).data.results;
              const failedInvoice = failedPayments?.find(
                invoice => invoice.invoice_type === "UGC_CAMPAIGN" && invoice.amount === totalCost,
              );

              if (failedInvoice) {
                getDefaultStore().set(paymentFailedModalDataAtom, [failedInvoice]);
              }
            }

            return Promise.reject(error);
          }
        },
        getCampaignType: () => (get().campaign.ugc ? "ugc" : get().campaign.tiktok ? "tiktok" : "instagram"),
        getCompanyPlanName: () => useUserStore.getState().user.package_subscription?.name,
        reset: () => set(initialState, false, "campaignManagementStore/reset"),
      }),
      {
        name: "useCampaignManagementStore",
      },
    ),
  ),
);

export default useCampaignManagementStore;

export const useManagedCampaignType: () => TCampaignType = () =>
  useCampaignManagementStore(state => (state.campaign.tiktok ? "tiktok" : state.campaign.ugc ? "ugc" : "instagram"));

export const useRequiredCampaignContentCount = () => {
  const requiredContent = useRequiredCampaignContent();
  const campaignType = useManagedCampaignType();

  const requiredContentCounts = useMemo(
    () => ({
      ...requiredContent,
      instagram: requiredContent.instagram.posts + requiredContent.instagram.reels + requiredContent.instagram.stories,
    }),
    [requiredContent],
  );

  return requiredContentCounts[campaignType];
};

export const useRequiredCampaignContent = () => {
  const campaign = useManagementCampaign() as TCampaign;

  const requiredContent = useMemo(
    () => ({
      ugc: campaign.number_of_ugc || 0,
      instagram: {
        stories: campaign.number_of_stories || 0,
        reels: campaign.number_of_reels || 0,
        posts: campaign.number_of_feedposts || 0,
      },
      tiktok: campaign.tiktok_number_of_medias || 0,
    }),
    [campaign],
  );
  return requiredContent;
};

export const useManagementCampaign = () => useCampaignManagementStore(state => state.campaign);
